import dayjs from 'dayjs';
import { fromEvent, Observable, of } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { IDateRange } from '../components/sr-datepicker/sr-datepicker.model';

export type dateOption =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

/**
 * @description emits sroll event from top.
 */
export const getScrollEvt = (): Observable<any> => {
  const container = document.querySelector('.sr-container');
  if (container) {
    return fromEvent(container, 'scroll').pipe(debounceTime(100));
  }

  return of(null);
};

/**
 * @description Download pdf by url
 */
export const downloadPdf = (url: string, filename: string): void => {
  // Construct the a element
  if (!url) {
    return;
  }

  const el = document.createElement('a');
  el.setAttribute('href', 'data:application/octet-stream;base64,' + url);
  el.setAttribute('download', filename);
  document.body.appendChild(el);
  el.click();
  document.body.removeChild(el);
};

/**
 * @description Download file by url
 */
export const downloadFileByUrl = (
  url: string,
  filename: string,
  target = '_blank'
): void => {
  if (!url) {
    return;
  }
  const link = document.createElement('a');
  link.download = filename;
  link.target = target;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * @description Return date based on date option
 */
export const getDateRange = (type: dateOption): IDateRange => {
  const now = dayjs();
  switch (type) {
    case 'today':
      return {
        start: formatDate(now),
        end: formatDate(now),
      };

    case 'yesterday':
      const yesterday = dayjs().subtract(1, 'day');
      return {
        start: formatDate(yesterday),
        end: formatDate(yesterday),
      };

    case 'last7days':
      const last7days = dayjs().subtract(6, 'day');
      return {
        start: formatDate(last7days),
        end: formatDate(now),
      };

    case 'last30days':
      const last30days = dayjs().subtract(29, 'day');
      return {
        start: formatDate(last30days),
        end: formatDate(now),
      };

    case 'thisMonth':
      const startMonth = dayjs().startOf('month');
      const endMonth = dayjs().endOf('month');
      return {
        start: formatDate(startMonth),
        end: formatDate(endMonth),
      };

    case 'lastMonth':
      const prevMonth = now.month() - 1;
      const startDate = dayjs().month(prevMonth).startOf('month');
      const endDate = dayjs().month(prevMonth).endOf('month');
      return {
        start: formatDate(startDate),
        end: formatDate(endDate),
      };

    case 'custom':
      return {
        start: '',
        end: '',
      };

    default:
      return {
        start: formatDate(now),
        end: formatDate(now),
      };
  }
};

/**
 * @description Return formatted date string
 * @param date: dayjs object
 */
export const formatDate = (date: dayjs.Dayjs): string => {
  return date.format('YYYY-MM-DD');
};

/**
 * @description Return user object from localstorage else empty object
 */
export const getUser = (): Record<string, any> => {
  return JSON.parse(localStorage.getItem('ngStorage-USER') || '{}');
};

/**
 * @description Return 4 digit random string
 */
export const uuid4 = (): string => {
  const randomNum = Math.floor(Math.random() * 1000000);
  return randomNum.toString(36);
};

/**
 * @description Return deep copy of object.
 */
export function deepCopy<T>(list: T): T {
  return JSON.parse(JSON.stringify(list));
}

export async function encryptData(payload: string, encryptionKey: string, encryptionIV: string) {
  // Convert encryption key and IV to Uint8Array
  const keyBytes = new TextEncoder().encode(encryptionKey);
  const ivBytes = new TextEncoder().encode(encryptionIV);

  // Import the raw key
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    {
      name: "AES-CBC",
      length: 256
    },
    false, // extractable
    ["encrypt"]
  );

  // Convert the payload to Uint8Array
  const payloadBytes = new TextEncoder().encode(payload);

  // Encrypt the data
  const encryptedBytes = await window.crypto.subtle.encrypt(
    {
      name: "AES-CBC",
      iv: ivBytes
    },
    cryptoKey,
    payloadBytes
  );

  // Convert the encrypted bytes to base64 string
  return btoa(String.fromCharCode(...new Uint8Array(encryptedBytes)));
}
